import React from 'react';
import {AppText, AppSubtitle, AppCardTitle, AppBodyText, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import Button from '../../../../Presentation/components/ui/Button';
import Card from '../../../../Presentation/components/ui/Card';
import {Permission} from '../../models/permission.model';
import {Check, X} from 'lucide-react';

interface PermissionSelectorProps {
  permissions: Permission[];
  selectedPermissions: number[];
  onPermissionToggle: (permissionId: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDeselectCategory?: (categoryPermissions: Permission[]) => void;
  getResourceDisplayName: (resource: string) => string;
  getActionDisplayName: (action: string) => string;
  category: string;
  loading?: boolean;
}

/**
 * Reusable component for selecting permissions
 * Clean, focused component with single responsibility
 */
export const PermissionSelector: React.FC<PermissionSelectorProps> = ({
  permissions,
  selectedPermissions,
  onPermissionToggle,
  onSelectAll,
  onDeselectAll,
  onDeselectCategory,
  getResourceDisplayName,
  getActionDisplayName,
  category,
  loading = false
}) => {
  const allSelected = permissions.every(p => selectedPermissions.includes(p.id));
  const someSelected = permissions.some(p => selectedPermissions.includes(p.id));

  // Category-specific deselect function
  const handleDeselectCategory = () => {
    if (onDeselectCategory) {
      onDeselectCategory(permissions);
    } else {
      // Fallback to global deselect if category-specific function is not provided
      onDeselectAll();
    }
  };

  if (loading) {
    return (
      <Card className="p-5">
        <div className="text-center py-8">
          <AppText className="text-gray-500">جاري التحميل...</AppText>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <AppCardTitle>{category}</AppCardTitle>
        <Button
          variant="outline"
          size="medium"
          onClick={allSelected ? handleDeselectCategory : onSelectAll}
        >
          {allSelected ? (
            <>
              <X className="w-5 h-5 ml-2" />
              إلغاء الكل
            </>
          ) : (
            <>
              <Check className="w-5 h-5 ml-2" />
              تحديد الكل
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {permissions.map(permission => {
          const isSelected = selectedPermissions.includes(permission.id);

          return (
            <div
              key={permission.id}
              className={`flex items-center space-x-3 space-x-reverse p-3 rounded-lg border cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-brand-50 border-brand-200'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => onPermissionToggle(permission.id)}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                isSelected
                  ? 'bg-brand-600 border-brand-600'
                  : 'border-gray-300'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>

              <div className="flex-1 min-w-0">
                <AppBodyText className="font-medium text-gray-900 truncate">
                  {getActionDisplayName(permission.action)}
                </AppBodyText>
                <AppSmallText className="text-gray-500 truncate">
                  {permission.description || `${permission.resource} - ${permission.action}`}
                </AppSmallText>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
